import { useEffect, useState } from 'react';
import { SalesOrderInfo } from '../../../store/slices/salesOrders';
import { useAppDispatch, useAppSelector } from '../../../store/store';
import { fetchVitalSigns, selectVitalSigns } from '../../../store/slices/vitalSigns';

import EditVitalSignsCard from './EditVitalSignCard';
import WithLoader from '../../../components/WithLoader/WithLoader';
import _ from 'lodash';
import classes from './VitalSignsTab.module.css';
import { useTranslation } from '../../../hooks/useTranslations';
import { vitalSignsGroupNames } from '../../../constants';

export type VitalSignResponse = {
  vitalSignResult?: {
    vital_sign_value_result: number;
    result_weight: string;
  };
};

type Props = {
  selectedSalesOrder: SalesOrderInfo | undefined;
  report?: boolean;
};

const VitalSignsTab: React.FC<Props> = ({ selectedSalesOrder, report }) => {
  const dispatch = useAppDispatch();
  const { translations } = useTranslation();
  const [isLoading, setIsLoading] = useState(true);
  const [addManualy, setAddManualy] = useState(false);

  useEffect(() => {
    setIsLoading(true);
    dispatch(fetchVitalSigns());
    setIsLoading(false);
  }, [dispatch]);

  const vitalSigns = useAppSelector(selectVitalSigns);

  let vitalSignsToShow: Array<any> = [];
  const filterBySevice = [...vitalSigns]?.filter((item: any) =>
    item?.services?.find(
      (service: any) => service.service_id === selectedSalesOrder?.service.service_id
    )
  );

  if (filterBySevice.find((el) => el.vital_sign_group_name === 'Blood Glucose')) {
    vitalSignsToShow = [
      ...filterBySevice,
      // These are hardcoded values, because the API does not return them
      {
        vital_sign_name: 'Mealtime',
        vital_sign_id: '',
        vital_sign_group_name: 'Blood Glucose',
        vital_sign_group_id: '5',
        services: [
          { service_id: '', service_name: 'Online Consulting', is_mandatory: true },
          { service_id: '', service_name: 'Healthcheck Consultation', is_mandatory: true },
          { service_id: '', service_name: 'Account Vital Signs', is_mandatory: true },
        ],
      },
      {
        vital_sign_name: 'Unit',
        vital_sign_id: '',
        vital_sign_group_name: 'Blood Glucose',
        vital_sign_group_id: '5',
        services: [
          { service_id: '', service_name: 'Online Consulting', is_mandatory: true },
          { service_id: '', service_name: 'Healthcheck Consultation', is_mandatory: true },
          { service_id: '', service_name: 'Account Vital Signs', is_mandatory: true },
        ],
      },
    ];
  } else {
    vitalSignsToShow = filterBySevice;
  }

  let groupedByGroupName = _.groupBy(vitalSignsToShow, 'vital_sign_group_name');

  function moveObjectToFirst(arr: Array<any>, key: string, value: string | number) {
    const index = arr?.findIndex((obj) => obj[key] === value);
    if (index > -1) {
      const [item] = arr.splice(index, 1); // Remove the object from its original position
      arr.unshift(item); // Add it to the beginning
    }
    return arr;
  }
  // Set Body Weight as the first item in the Body Composition group
  if (groupedByGroupName['Body Composition']) {
    groupedByGroupName['Body Composition'] = moveObjectToFirst(
      groupedByGroupName['Body Composition'],
      'vital_sign_name',
      'Body Weight'
    );
  }

  return (
    <div
      className={classes.container}
      style={
        report
          ? {
            flexDirection: 'column',
            marginLeft: 'auto',
            marginRight: 'auto',
          }
          : {}
      }
    >
      <WithLoader isLoading={isLoading && groupedByGroupName} />
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          gap: '10px',
          alignItems: 'center',
          width: '100%',
        }}
      >
        <p className={classes.paragraph}>Results</p>
        {selectedSalesOrder?.work_order?.work_order_status_id !== 2 && (
          <button className={classes['btn-transparent']} onClick={() => setAddManualy(!addManualy)}>
            {!addManualy ? translations('vitalsigns.manual.add') : translations('back')}
          </button>
        )}
      </div>
      {Object.keys(groupedByGroupName).map((serviceName) => {
        if (!vitalSignsGroupNames.includes(serviceName)) return null;
        return (
          <EditVitalSignsCard
            key={serviceName}
            serviceId={serviceName}
            item={groupedByGroupName[serviceName]}
            selectedSalesOrder={selectedSalesOrder}
            addManualy={addManualy}
          />
        );
      })}
      <WithLoader />
    </div>
  );
};

export { VitalSignsTab };
